import { useMemo, useState } from "react";
import type { PatternHole, HoleCompletion, HoleCompletionStatus } from "@/types/pattern";

interface PatternMapProps {
  holes: PatternHole[];
  completions: HoleCompletion[];
  compact?: boolean;
}

const STATUS_COLORS: Record<HoleCompletionStatus | "pending", string> = {
  pending: "#6b7280",
  completed: "#22c55e",
  "in-progress": "#f59e0b",
  skipped: "#3b82f6",
  redrilled: "#ef4444",
};

export default function PatternMap({ holes, completions, compact = false }: PatternMapProps) {
  const [tooltip, setTooltip] = useState<{ holeId: string; x: number; y: number } | null>(null);

  const { points, viewBox } = useMemo(() => {
    if (holes.length === 0) return { points: [], viewBox: "0 0 100 100" };

    let minE = Infinity, maxE = -Infinity, minN = Infinity, maxN = -Infinity;
    for (const h of holes) {
      if (h.collarEasting < minE) minE = h.collarEasting;
      if (h.collarEasting > maxE) maxE = h.collarEasting;
      if (h.collarNorthing < minN) minN = h.collarNorthing;
      if (h.collarNorthing > maxN) maxN = h.collarNorthing;
    }

    const rangeE = maxE - minE || 1;
    const rangeN = maxN - minN || 1;
    const pad = 0.08;
    const padE = rangeE * pad;
    const padN = rangeN * pad;
    const width = 800;
    const scaleX = (width - padE * 2) / rangeE;
    const scaleY = (width - padN * 2) / rangeN;
    const scale = Math.min(scaleX, scaleY);
    const h = Math.round(padN * 2 + rangeN * scale);

    const mapped = holes.map((hole) => ({
      hole,
      x: padE + (hole.collarEasting - minE) * scale,
      y: padN + (maxN - hole.collarNorthing) * scale,
    }));

    return { points: mapped, viewBox: `0 0 ${width} ${h}` };
  }, [holes]);

  const completionMap = useMemo(() => {
    const map = new Map<string, HoleCompletionStatus>();
    for (const c of completions) map.set(c.holeId, c.status);
    return map;
  }, [completions]);

  const tooltipHole = tooltip ? holes.find((h) => h.holeId === tooltip.holeId) : null;
  const tooltipCompletion = tooltip
    ? completions.filter((c) => c.holeId === tooltip.holeId).at(-1)
    : null;

  return (
    <div className="w-full">
      <svg
        viewBox={viewBox}
        className={compact ? "w-full max-h-48" : "w-full max-h-[50vh]"}
        preserveAspectRatio="xMidYMid meet"
        style={{ background: "hsl(var(--card))", borderRadius: 8 }}
      >
        {points.map(({ hole, x, y }) => {
          const status = completionMap.get(hole.holeId) ?? "pending";
          const color = STATUS_COLORS[status];
          const r = compact ? 3 : hole.holeType === "production" ? 6 : 5;

          return (
            <g
              key={hole.holeId}
              style={{ cursor: "pointer" }}
              onClick={() =>
                setTooltip((prev) =>
                  prev?.holeId === hole.holeId ? null : { holeId: hole.holeId, x, y }
                )
              }
            >
              <circle cx={x} cy={y} r={compact ? 6 : 12} fill="transparent" />
              {hole.holeType === "production" ? (
                <circle cx={x} cy={y} r={r} fill={color} />
              ) : hole.holeType === "batter" ? (
                <rect x={x - r} y={y - r} width={r * 2} height={r * 2} fill={color} />
              ) : hole.holeType === "buffer" ? (
                <rect x={x - r} y={y - r} width={r * 2} height={r * 2} fill={color} transform={`rotate(45 ${x} ${y})`} />
              ) : (
                <polygon points={`${x},${y - r} ${x + r},${y + r} ${x - r},${y + r}`} fill={color} />
              )}
            </g>
          );
        })}

        {tooltipHole && tooltip && !compact && (
          <g>
            <rect
              x={tooltip.x + 10}
              y={tooltip.y - 55}
              width={200}
              height={65}
              rx={4}
              fill="hsl(var(--popover))"
              stroke="hsl(var(--border))"
              strokeWidth={1}
            />
            <text x={tooltip.x + 18} y={tooltip.y - 37} fontSize={11} fill="hsl(var(--popover-foreground))" fontWeight="bold">
              {tooltipHole.holeId} ({tooltipHole.holeType}) — {tooltipHole.designedDepth}m, {tooltipHole.diameter}mm
            </text>
            <text x={tooltip.x + 18} y={tooltip.y - 22} fontSize={10} fill="hsl(var(--muted-foreground))">
              Status: {completionMap.get(tooltipHole.holeId) ?? "pending"}
            </text>
            {tooltipCompletion && (
              <text x={tooltip.x + 18} y={tooltip.y - 8} fontSize={10} fill="hsl(var(--muted-foreground))">
                Drilled by {tooltipCompletion.operatorName}
                {tooltipCompletion.actualDepth != null ? ` — ${tooltipCompletion.actualDepth.toFixed(1)}m actual` : ""}
              </text>
            )}
          </g>
        )}
      </svg>

      {!compact && (
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground justify-center">
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <span key={status} className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color }} />
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
